import { Footer } from "@/components/LandingPage/Footer";
import { GetStarted } from "@/components/LandingPage/GetStarted";
import Navbar from "@/components/LandingPage/Navbar";
import { TestimonialsSection } from "@/components/LandingPage/TestimonialsSection";
import { prisma } from "@/server/prisma";
import { state } from "@/state";
import { Box, Button, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import featuresRectangle from "@root/public/landing/features-rectangle.svg";
import type { Metadata } from "next";
import { useTranslations } from "next-intl";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useSnapshot } from "valtio";
import heroBg from "@root/public/images/photos/clarifayo_montanha.jpg";

export const metadata: Metadata = {
  title: "Clarifayo",
  description: "Terms and Conditions",
};

const Hero = () => {
  const t = useTranslations();
  const stateSnap = useSnapshot(state);

  const { role } = stateSnap;

  return (
    <Stack
      gap={{ base: 8, md: 14 }}
      py={{ base: 32, md: 20 }}
      px={{
        base: 8,
        md: 20,
      }}
      bgColor="brand.1000"
      minH={{
        base: "90vh",
      }}
      position="relative"
      overflow={"hidden"}
      backgroundImage={`url(${heroBg.src})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundAttachment="fixed"
    >
      <Box className="hero-bg"></Box>
      <Box position="absolute" top={0} left={0} w="full">
        <Navbar />
      </Box>
      <Box className="flex-1"></Box>
      <Stack
        as={Box}
        direction={["column", "row"]}
        textAlign={"left"}
        alignItems="center"
        flex={1}
        gap={{
          base: 8,
          md: 4,
        }}
        position="relative"
        h="full"
      >
        <Image
          src={featuresRectangle}
          alt="Rectangles"
          style={{
            position: "absolute",
            left: "-40px",
            bottom: "-100px",
            zIndex: -0,
            objectFit: "cover",
          }}
        />
        <Box flex={2} zIndex={1}>
          <Heading
            color={"brand.100"}
            fontFamily={"Gotham"}
            letterSpacing={"1"}
            fontWeight={600}
            fontSize={{ base: "3xl", sm: "4xl", md: "5xl" }}
            lineHeight={"110%"}
            textAlign={{ base: "center", md: "left" }}
          >
            {t("termsOfService")}
          </Heading>
        </Box>
        <Box flex={3} textAlign={"center"} h="full" zIndex={1}>
          <Heading
            color="white"
            fontSize={{ base: "3xl", sm: "4xl", md: "5xl" }}
            lineHeight={"110%"}
            textAlign={"center"}
          >
            Clarifayo
          </Heading>
          <Text color={"brand.100"} mt={6} fontSize={24}>
            {t(role === "client" ? "iAmClient" : "iAmCoach")}
          </Text>
          <Button
            variant="link"
            onClick={() =>
              (state.role = role === "client" ? "coach" : "client")
            }
            mt={2}
            fontSize={16}
          >
            {t(role === "client" ? "notAClient" : "notACoach")}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};

const ContentViewer = (props: any) => {
  return (
    <Flex w="full" flexDirection={"column"} gap={4}>
      <div
        dangerouslySetInnerHTML={{
          __html: props.content?.value?.replace(
            /<style[^>]*>.*?<\/style>/gs,
            ""
          ) ?? "",
        }}
      ></div>
    </Flex>
  );
};

const keys = {
  client: "TERMS_AND_CONDITIONS_CLIENT",
  coach: "TERMS_AND_CONDITIONS_COACH",
};

const ClientContent = () => {
  const stateSnap = useSnapshot(state);

  const { lang, appContent } = stateSnap;

  const content = appContent.find(
    (c: { key: string; language: string }) =>
      c.key === keys.client && c.language === lang
  );

  return <ContentViewer content={content} />;
};

const CoachContent = () => {
  const stateSnap = useSnapshot(state);

  const { lang, appContent } = stateSnap;

  const content = appContent.find(
    (c: { key: string; language: string }) =>
      c.key === keys.coach && c.language === lang
  );

  return <ContentViewer content={content} />;
};

const Content = (props: any) => {
  const router = useRouter();
  const { role } = useSnapshot(state);

  useEffect(() => {
    if (!!router.query.role && router.query.role !== role) {
      state.role = router.query.role as "client" | "coach";
    }

    const userLang = router.pathname.includes("/en/") ? "en" : "pt";
    state.lang = userLang;
  }, [router, role]);

  state.appContent = props.contentList;

  return (
    <>
      <Stack
        py={{ base: 20, md: 20 }}
        px={{
          base: 8,
          md: 20,
        }}
        bgColor="brand.100"
      >
        {role === "client" ? <ClientContent /> : <CoachContent />}
      </Stack>
    </>
  );
};

export default function Page(props: any) {
  return (
    <>
      <Head>
        <title>Clarifayo - maximize your Coaching Activity</title>
      </Head>
      <Box bgColor="brand.100">
        <Hero />
        <Content contentList={props.contentList} />
        <TestimonialsSection />
        <GetStarted />
        <Footer />
      </Box>
    </>
  );
}

export async function getServerSideProps() {
  const contentList = await prisma.appContent.findMany({
    where: {
      key: {
        in: [keys.client, keys.coach] as any[],
      },
    },
    select: {
      id: true,
      key: true,
      value: true,
      language: true,
    },
  });
  return {
    props: {
      contentList: contentList ?? [],
    },
  };
}
